import React, {useState, useEffect, useContext, useRef, useCallback} from "react";
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import {CrossIcon} from "@common/icons/";
import {Select} from "antd";
import IconButton from "@common/IconButton";
import NavigateCourse from "./NavigateCourse";
import useOnClickOutside from "@hooks/useOnClickOutside";
import {FocusTrap} from "focus-trap-react";
import {useScreenMode} from "@common/context/ScreenModeContext";

const SeriesNavigation = props => {
    const {visible, disabled = false, outsideClickIgnoreClass, callback} = props;
    const {user, setUser, updateClass} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const seriesNavigationRef = useRef();
    const [syllabusGroups, setSyllabusGroups] = useState([]);
    const [syllabusGroupId, setSyllabusGroupId] = useState();
    const [syllabusId, setSyllabusId] = useState();
    const [syllabusGroupSelectProps, setSyllabusGroupSelectProps] = useState({options: [], defaultValue: undefined});
    const [syllabusesSelectProps, setSyllabusesSelectProps] = useState({options: [], defaultValue: undefined});
    const [classOptions, setClassOptions] = useState([]);
    const [classOption, setClassOption] = useState();
    const [seriesDropdownVisible, setSeriesDropdownVisible] = useState(true);
    const selectRef = useRef();
    const syllabusSelectRef = useRef();
    const hasFocusedOnMount = useRef(false);
    const {smallScreenMode, mobileScreenMode} = useScreenMode();

    useOnClickOutside(seriesNavigationRef, visible, (event) => {
        if (event.type === 'focus' || event.type === 'focusin' || event.type === 'focusout' || event instanceof FocusEvent) {
            return;
        }

        if(event.target.classList.contains('ant-select-item-option-content') || event.target.classList.contains(outsideClickIgnoreClass) || event.target.closest('.' + outsideClickIgnoreClass) != undefined) {
            return;
        }
        callback();
    });

    const loadSyllabusGroups = () => {
        axios.get('/web/ui/nav/syllabusesGroups')
            .then(response => {
                let newSyllabusGroupSelectProps = {
                    defaultSelected: undefined,
                    options: []
                };
                response.data.map((syllabusGroup, index) => {
                    let option = {
                        value: syllabusGroup.syllabusGroupId,
                        label: syllabusGroup.syllabusGroupName
                    }

                    if (syllabusGroup.syllabusGroupId === user.syllabusGroupId) newSyllabusGroupSelectProps.defaultSelected = index;
                    newSyllabusGroupSelectProps.options.push(option);
                });
                if (newSyllabusGroupSelectProps.defaultSelected === undefined) newSyllabusGroupSelectProps.defaultSelected = 0;
                setSyllabusGroups(response.data);
                setSyllabusGroupSelectProps(newSyllabusGroupSelectProps);
            });
    }

    const handleChangeSyllabusGroup = (syllabusGroupId, initialLoad) => {
        syllabusGroups.map(syllabusGroup => {
            if (syllabusGroup.syllabusGroupId === syllabusGroupId) {
                let newSyllabusesSelectProps = {
                    defaultSelected: 0,
                    options: []
                };
                syllabusGroup.syllabuses.map((syllabus, index) => {
                    let option = {
                        value: syllabus.syllabusId,
                        label: syllabus.syllabusName
                    }

                    if (syllabus.syllabusId === user.syllabusId) newSyllabusesSelectProps.defaultSelected = index;
                    newSyllabusesSelectProps.options.push(option);
                });
                if(initialLoad || syllabusGroups.length === 1) {
                    setSyllabusesSelectProps(newSyllabusesSelectProps);
                } else if(syllabusGroup.syllabuses.length > 0) {
                    window.location = '/dashboard/changeSyllabusMenu.action?syllabusId=' + syllabusGroup.syllabuses[0].syllabusId + '&syllabusGroupId=' + syllabusGroup.syllabusGroupId + '&syllabusChanged=false';
                }
            }
        });
        setSyllabusGroupId(syllabusGroupId);
    }

    const handleChangeSyllabus = (syllabusId, initialLoad) => {
        if (!syllabusId) {
            return;
        }
        if (initialLoad) {
            setSyllabusId(syllabusId);
        } else {
            window.location = '/dashboard/changeSyllabusMenu.action?syllabusId=' + syllabusId + '&syllabusGroupId=' + syllabusGroupId + '&syllabusChanged=true';
        }
    };

    const onChangeClass = (updateClassId) => {
        if (updateClassId === undefined || updateClassId <= 0) {
            return;
        }

        updateClass(updateClassId);
    };

    const handleToggleSeriesDropdown = (bool) => {
        setSeriesDropdownVisible(bool)
    };

    useEffect(() => {
        const escapeListener = e => {
            if (visible && e.key === 'Escape') {
                callback();
                document.getElementsByClassName('ch-series-select')[0].focus();
            }
            if (!visible) window.removeEventListener('keydown', escapeListener);
        }
        window.addEventListener('keydown', escapeListener);
        return () => {
            window.removeEventListener('keydown', escapeListener);
        }
    });

    useEffect(() => {
        if(user.classes != undefined) {
            const updatedClassOptions = user.classes.map(clazz => ({ value: clazz.classId, label: clazz.className }));
            const updatedClass = (user.classes.length > 0) ? (user.classId ? user.classes.filter(clazz => clazz.classId === user.classId)[0] :user.classes[0]) : undefined;
            const classOption = (updatedClass == undefined) ? undefined : {value: updatedClass.classId, label: updatedClass.className};

            setClassOptions(updatedClassOptions);
            setClassOption(classOption);
        }

        if (user.syllabusId !== undefined) {
            if (!syllabusGroupId) setSyllabusGroupId(user.syllabusGroupId);
            if (!syllabusId) setSyllabusId(user.syllabusId);

            loadSyllabusGroups();
        }
    }, [user]);

    useEffect(() => {
        if (syllabusGroupSelectProps.options.length > 0 && syllabusGroupSelectProps.defaultSelected !== undefined) {
            handleChangeSyllabusGroup(syllabusGroupSelectProps.options[syllabusGroupSelectProps.defaultSelected].value, true);
        }
    }, [syllabusGroupSelectProps, user.classId]);

    useEffect(() => {
        if (syllabusesSelectProps.options.length > 0) {
            handleChangeSyllabus(syllabusesSelectProps.options[syllabusesSelectProps.defaultSelected].value, true);
        }
    }, [syllabusesSelectProps]);

    useEffect(() => {
        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        // Sleep is here to give it a few ms for the original button press to fire and set focus AND
        // THEN set the focus to this field.
        const focusWithDelay = async () => {
            await sleep(100);
            if (visible) {
                const urlParams = new URLSearchParams(window.location.search);
                const syllabusChanged = urlParams.get('syllabusChanged');
                if (syllabusChanged === 'true' && syllabusSelectRef.current) {
                    syllabusSelectRef.current.focus();
                } else if (selectRef.current) {
                    selectRef.current.focus();
                }
            }
        };

        focusWithDelay();
    }, [visible]);

    const selectRefCallback = useCallback((node) => {
        selectRef.current = node;
        if (node && !hasFocusedOnMount.current) {
            const urlParams = new URLSearchParams(window.location.search);
            const syllabusChanged = urlParams.get('syllabusChanged');
            if (urlParams.get('showNavigation') === 'true' && syllabusChanged !== 'true') {
                node.focus();
                hasFocusedOnMount.current = true;
            }
        }
    }, []);

    const syllabusSelectRefCallback = useCallback((node) => {
        syllabusSelectRef.current = node;
        if (node && !hasFocusedOnMount.current) {
            const urlParams = new URLSearchParams(window.location.search);
            const syllabusChanged = urlParams.get('syllabusChanged');
            if (urlParams.get('showNavigation') === 'true' && syllabusChanged === 'true') {
                node.focus();
                hasFocusedOnMount.current = true;
            }
        }
    }, []);

    return (
        <FocusTrap
            active={visible && smallScreenMode}
        >
            <div id="course-selection-panel"
                 ref={seriesNavigationRef}
                 aria-label="Course Selection"
                 className={'ch-nav-column ch-series-drawer' + (visible ? ' ch-series-drawer-visible' : ' ch-series-drawer-hidden')}>
                {smallScreenMode &&
                    <div className={'ch-nav-column ch-nav-drawer-menu padding-t--m'}>
                        <IconButton label='content navigation' onClick={callback} size="large" aria-label='Close drawer'>
                            <CrossIcon/>
                        </IconButton>
                    </div>
                }
                <div className={'ch-nav-column ch-series-drawer-content'}>
                    {(seriesDropdownVisible || !mobileScreenMode) &&
                        <header className={'ch-series-nav-head padding--m'}>
                            <div className={'ch-emph-block padding--m'}>
                                <div>
                                    <label><span className="ch-input-label">{`${brandConfig.syllabusName} list`}</span>
                                        <Select
                                            style={{width: '100%'}}
                                            options={syllabusGroupSelectProps.options}
                                            value={syllabusGroupSelectProps.options[syllabusGroupSelectProps.defaultSelected]}
                                            onChange={value => {
                                                handleChangeSyllabusGroup(value, false);
                                            }}
                                            ref={selectRefCallback}
                                            getPopupContainer={trigger => trigger.parentElement}
                                            // Firefox's scroll model sends discrete jumps rather than smooth pixel events, causing
                                            // jerky scrolling inside AntD's JS-driven virtual list. Native DOM scroll (virtual=false)
                                            // fixes this but shows a scrollbar in Chrome, so we only disable it for Firefox.
                                            virtual={!navigator.userAgent.includes('Firefox')}
                                        />
                                    </label>
                                </div>
                                <div className={'margin-t--m'}>
                                    <label><span className="ch-input-label">{brandConfig.syllabusesName}</span>
                                        <Select
                                            style={{width: '100%'}}
                                            options={syllabusesSelectProps.options}
                                            value={syllabusesSelectProps.options[syllabusesSelectProps.defaultSelected]}
                                            onChange={value => {
                                                handleChangeSyllabus(value, false);
                                            }}
                                            ref={syllabusSelectRefCallback}
                                            getPopupContainer={trigger => trigger.parentElement}
                                        />
                                    </label>
                                </div>
                                {brandConfig.disableConcepts && user.teacher && user.classes !== undefined && user.classes.length > 0 &&
                                    <div className={'margin-t--m'}>
                                        <Select
                                            label={'Classes'}
                                            options={classOptions}
                                            value={classOption}
                                            onChange={value => {
                                                onChangeClass(value);
                                            }}
                                        />
                                    </div>
                                }
                            </div>
                        </header>}
                    <div className={'ch-series-menu-body padding-b--m'}>
                        <NavigateCourse
                            syllabusId={syllabusId}
                            classId={user.classId}
                            callback={callback}
                            visible={visible}
                            handleToggleSeriesDropdown={handleToggleSeriesDropdown}
                            mobileScreenMode={mobileScreenMode}/>
                    </div>
                </div>
                {!smallScreenMode &&
                    <div
                        tabIndex={0}
                        className={'ch-focus-sentinel'}
                        onFocus={() => {
                            const seriesSelectButton = document.querySelector('.ch-series-select');
                            if (seriesSelectButton) {
                                seriesSelectButton.focus();
                            }
                        }}
                    />
                }
            </div>
        </FocusTrap>
    );
}

export default SeriesNavigation;